import { useRef, useState } from "react";
import Header from "@/atomic/atoms/Header";
import Alert from "@/atomic/atoms/Alert";
import Loading from "@/atomic/atoms/Loading";
import Modal from "@/atomic/atoms/Modal";
import TicketModal from "@/atomic/organisms/TicketModal";
import ScrapersConsultationCard from "@/atomic/molecules/ScrapersConsultationCard";
import PlanSelection from "@/atomic/organisms/PlanSelection";
import useI18n from "@/hooks/useI18n";
import { TAG_CHECKOUT_URL } from "@/domain/scrapers/concessionaires";
import { PropsView } from "./Models";

type SuccessStep = "success" | "livre" | "plans";

const TAG_BENEFITS = [
  {
    titleKey: "pages:success:tagBenefitSaveTitle",
    descriptionKey: "pages:success:tagBenefitSaveDescription",
    iconSrc: "/svg/icon-double-coins.svg",
    iconBoxClass: "bg-info-muted",
  },
  {
    titleKey: "pages:success:tagBenefitAutoPayTitle",
    descriptionKey: "pages:success:tagBenefitAutoPayDescription",
    iconSrc: "/svg/icon-notification.svg",
    iconBoxClass: "bg-[#f9e6e6]",
  },
  {
    titleKey: "pages:success:tagBenefitFrequentTitle",
    descriptionKey: "pages:success:tagBenefitFrequentDescription",
    iconSrc: "/svg/icon-star.svg",
    iconBoxClass: "bg-info-muted",
  },
  {
    titleKey: "pages:success:tagBenefitNoMonthlyTitle",
    descriptionKey: "pages:success:tagBenefitNoMonthlyDescription",
    iconSrc: "/svg/icon-loan-check.svg",
    iconBoxClass: "bg-[#f9e6e6]",
  },
  {
    titleKey: "pages:success:tagBenefitNoFeeTitle",
    descriptionKey: "pages:success:tagBenefitNoFeeDescription",
    iconSrc: "/svg/icon-gift.svg",
    iconBoxClass: "bg-info-muted",
  },
] as const;

const LIVRE_STEPS = [
  {
    titleKey: "pages:success:livreStepOrder",
    descriptionKey: "pages:success:livreStepOrderDescription",
  },
  {
    titleKey: "pages:success:livreStepStick",
    descriptionKey: "pages:success:livreStepStickDescription",
  },
  {
    titleKey: "pages:success:livreStepReady",
    descriptionKey: "pages:success:livreStepReadyDescription",
  },
] as const;

function CheckCircleIcon() {
  return (
    <div className="w-10 h-10 bg-success-muted border border-[#24d696] rounded-theme-small flex items-center justify-center p-2 shrink-0">
      <img
        src="/svg/icon-check-green.svg"
        alt=""
        width={24}
        height={24}
      />
    </div>
  );
}

export default function AvulsoSuccessView({
  goBack,
  openInvoice,
  onNotNow,
  consultation,
  invoice,
  toggleModal,
  generatePDF,
  isVisibleModal,
  isLoading,
  error,
  toggleError,
  handleTryAgain,
  toggleModalRegister,
}: PropsView) {
  const { t, Trans } = useI18n();
  const [step, setStep] = useState<SuccessStep>("success");
  const tagOfferRef = useRef<HTMLElement>(null);
  const hasScraperPassages = Boolean(consultation?.hasAnyPassage);

  const headerTitle =
    step === "plans"
      ? t("pages:success:chooseAPlan")
      : step === "livre"
        ? t("pages:success:electronicTollHeader")
        : t("pages:checkout:payment");

  const handleHeaderBack = () => {
    if (step === "plans") {
      setStep("livre");
      return;
    }
    if (step === "livre") {
      setStep("success");
      return;
    }
    goBack();
  };

  const handleAutomatePayments = () => {
    tagOfferRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="relative flex flex-col min-h-screen bg-background">
      <Header title={headerTitle} hasLeftButton goBack={handleHeaderBack} />

      <main
        className={`flex-1 overflow-y-auto ${
          step === "plans"
            ? "px-0 pt-0 pb-0 flex flex-col"
            : "px-4 pt-4 pb-8"
        }`}
      >
        {step === "plans" ? (
          <PlanSelection layout="stack" />
        ) : (
        <div className="w-full max-w-[480px] mx-auto flex flex-col gap-6">
          {step === "success" && (
            <>
              <Alert variant="warning">{t("pages:home:warning48h")}</Alert>
              <section className="flex flex-col items-center text-center gap-3 pt-4">
                <CheckCircleIcon />
                <h2 className="text-[22px] font-bold font-primary text-text leading-[1.1] tracking-[-0.1px]">
                  {t("pages:success:paymentCompletedTitle")}
                </h2>
                <button
                  type="button"
                  onClick={openInvoice}
                  className="text-base font-light font-primary text-primary underline tracking-[-0.1px] leading-[1.4]"
                >
                  {t("pages:success:openReceipt")}
                </button>
                <p className="text-base font-normal font-primary text-[#525252] leading-[1.4]">
                  {t("pages:success:paymentCompletedSupport")}
                </p>
                <button
                  type="button"
                  onClick={handleAutomatePayments}
                  className="text-base font-light font-primary text-text underline tracking-[-0.1px] leading-[1.4]"
                >
                  {t("pages:success:tagAutomatePayments")}
                </button>
              </section>

              <div className="w-full h-px bg-border" />

              {hasScraperPassages && consultation && (
                <section className="flex flex-col gap-4">
                  <p className="text-base font-medium font-secondary text-text text-center tracking-[-0.4px] leading-[20.8px]">
                    {t("pages:home:passagesInOtherConcessionaires")}
                  </p>
                  <Alert variant="highlight" showIcon={false}>
                    {t("pages:success:passagesPayOnConcessionaireSites")}
                  </Alert>
                  <ScrapersConsultationCard consultation={consultation} />
                </section>
              )}

              <section
                ref={tagOfferRef}
                data-testid="tag-offer-card"
                className="flex flex-col gap-4"
              >
                <div className="bg-foreground rounded-2xl px-4 py-5 flex flex-col gap-4">
                  <p className="text-base font-normal font-primary text-text leading-[1.4] tracking-[-0.1px]">
                    <Trans
                      i18nKey="pages:success:tagOfferTitle"
                      components={[
                        <span className="font-bold" />,
                        <span className="font-bold" />,
                      ]}
                    />
                  </p>
                  <div className="h-px w-full bg-[#e6e6e6]" />
                  {TAG_BENEFITS.map((benefit, index) => (
                    <div key={benefit.titleKey} className="flex flex-col gap-4">
                      <div className="flex items-center gap-4">
                        <span
                          aria-hidden
                          className={`w-10 h-10 rounded-theme-small shrink-0 flex items-center justify-center p-2 ${benefit.iconBoxClass}`}
                        >
                          <img
                            src={benefit.iconSrc}
                            alt=""
                            width={24}
                            height={24}
                          />
                        </span>
                        <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                          <p className="text-base font-bold font-primary text-text leading-[1.4] tracking-[-0.1px]">
                            {t(benefit.titleKey)}
                          </p>
                          <p className="text-sm font-normal font-primary text-[#525252] leading-[1.4] tracking-[-0.1px]">
                            {t(benefit.descriptionKey)}
                          </p>
                        </div>
                      </div>
                      {index < TAG_BENEFITS.length - 1 && (
                        <div className="h-px w-full bg-[#e6e6e6]" />
                      )}
                    </div>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={() => setStep("livre")}
                  className="w-full h-[60px] bg-primary text-white rounded-theme-large font-primary text-base font-medium"
                >
                  {t("pages:success:guaranteeFreeTag")}
                </button>
                <button
                  type="button"
                  onClick={onNotNow}
                  className="w-full h-[60px] border border-text bg-foreground text-text rounded-theme-large font-primary text-base font-medium"
                >
                  {t("pages:home:noPassagesNotNow")}
                </button>
              </section>
            </>
          )}

          {step === "livre" && (
            <section className="pt-4 pb-2">
              <div className="flex flex-col gap-3">
                <article className="bg-foreground border border-[#e6e6e6] rounded-2xl overflow-hidden">
                  <div className="flex items-center gap-2.5 px-6 py-4">
                    <h2 className="flex-1 text-lg font-bold font-primary text-text leading-[1.2] tracking-[-0.1px]">
                      {t("pages:success:livrePlanTitle")}
                    </h2>
                    <span className="bg-primary text-white text-sm font-bold font-primary px-1 py-0 leading-[1.4] rounded whitespace-nowrap">
                      {t("pages:success:newBadge")}
                    </span>
                  </div>
                  <div className="h-px bg-border" />
                  <div className="px-5 py-4 flex flex-col gap-3">
                    <p className="text-base font-bold font-primary text-text leading-[1.3]">
                      {t("pages:success:livrePlanDescription")}
                    </p>
                    <span className="self-start inline-flex items-center gap-1.5 bg-text text-white text-xs font-bold font-primary px-2.5 py-1 rounded-md">
                      <img
                        src="/svg/icon-truck.svg"
                        alt=""
                        width={12}
                        height={12}
                        className="w-3 h-3 shrink-0"
                      />
                      {t("pages:success:livreFreeShipping")}
                    </span>
                  </div>
                </article>

                {LIVRE_STEPS.map((stepItem, index) => (
                  <article
                    key={stepItem.titleKey}
                    className="bg-foreground border border-[#e6e6e6] rounded-theme-large p-4 flex items-center gap-4"
                  >
                    <span
                      data-testid="livre-step-number"
                      className="w-10 h-10 rounded-theme-small bg-[#e5729b] text-white text-xl font-bold font-primary flex items-center justify-center shrink-0 tracking-[-0.1px]"
                    >
                      {index + 1}
                    </span>
                    <div className="min-w-0 flex-1 flex flex-col gap-1">
                      <p className="text-base font-bold font-primary text-text leading-[1.4] tracking-[-0.1px]">
                        {t(stepItem.titleKey)}
                      </p>
                      <p className="text-sm font-normal font-primary text-[#696966] leading-[1.4] tracking-[-0.1px]">
                        {t(stepItem.descriptionKey)}
                      </p>
                    </div>
                  </article>
                ))}

                <a
                  href={TAG_CHECKOUT_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 w-full h-[56px] bg-primary text-white rounded-2xl font-primary text-base font-bold flex items-center justify-center"
                >
                  {t("pages:success:activateNow")}
                </a>
                <button
                  type="button"
                  onClick={() => setStep("plans")}
                  className="w-full h-[56px] bg-foreground border border-text text-text rounded-2xl font-primary text-base font-bold"
                >
                  {t("pages:success:seeAllPlans")}
                </button>
              </div>
            </section>
          )}
        </div>
        )}
      </main>

      {isLoading && <Loading />}
      {isVisibleModal && invoice && (
        <TicketModal
          invoice={invoice}
          generatePDF={generatePDF || (() => undefined)}
          toggleModal={toggleModal || (() => undefined)}
          toggleModalRegister={toggleModalRegister || (() => undefined)}
        />
      )}
      {error && (
        <Modal
          title="Ops"
          message={t("pages:receipt:description")}
          actionMessage={t("pages:receipt:checkAgain")}
          action={handleTryAgain}
          actionSecond={toggleError}
          actionSecondMessage={t("common:close")}
        />
      )}
    </div>
  );
}
